import React, { useCallback } from 'react'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { useRequests } from '../requests/useRequests'
import { RefreshActions } from '../state/refresh'
import { Subject } from '../types/subject'
import { State } from '../types/state'
// import { Checkbox, FormControl } from '@material-ui/core'
import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel
} from '@mui/material'

interface NewOrEditSubjectDialogProps {
  subject: Subject | null
  handleClose: (b: boolean) => void
}

const NewOrEditSubjectDialog = ({
  subject,
  handleClose
}: NewOrEditSubjectDialogProps): JSX.Element => {
  const dispatch = useDispatch()
  const requestMaker = useRequests()
  const { app } = useSelector((state: State) => state.user)

  const initSubject = {
    id: subject?.id,
    name: subject?.name,
    minSLUsersNumber: subject?.minSLUsersNumber,
    minHLUsersNumber: subject?.minHLUsersNumber,
    indexes: subject?.indexes,
    qualification: subject?.qualification,
    isOnlySL: !!subject?.isOnlySL,
    isOnlyHL: !!subject?.isOnlyHL
  }

  const validate = (subject: Partial<Subject>): Partial<Subject> => {
    const errors: Partial<Subject> = {}

    if (!subject?.name || subject.name === '') {
      errors.name = 'Name cannot be empty.'
    }

    if (!subject?.minSLUsersNumber && subject.minSLUsersNumber !== 0) {
      errors.minSLUsersNumber = 'Min SL users number cannot be empty.' as any
    }

    if (!subject?.minHLUsersNumber && subject.minHLUsersNumber !== 0) {
      errors.minHLUsersNumber = 'Min HL users number cannot be empty.' as any
    }

    if (subject?.indexes == null || subject.indexes.length === 0) {
      errors.indexes = 'Choose at least one index.' as any
    }

    return errors
  }

  const onSubmit = useCallback(
    async (subject: Partial<Subject>): Promise<void> => {
      try {
        const isEdited = subject.id !== undefined

        const indexesSubject = {
          ...subject,
          indexes: subject.indexes?.map(i => i.index) ?? []
        }

        if (isEdited) {
          await requestMaker.updateSubject(indexesSubject as any)
        } else {
          await requestMaker.createSubject(indexesSubject as any)
        }
        dispatch(RefreshActions.refreshUserState())
        dispatch(
          RefreshActions.setToastMessage({
            message: 'Succesfully update subject'
          })
        )
        handleClose(true)
      } catch (e) {
        dispatch(
          RefreshActions.setToastMessage({
            message:
              'Error - fill all neccessary fields correctly and try again'
          })
        )
      }
    },
    [requestMaker, dispatch]
  )

  const formik = useFormik({
    initialValues: initSubject,
    onSubmit,
    enableReinitialize: true,
    validate
  })

  const subjectsToSelectNumber =
    (app?.highLevelSubjectNumber ?? 0) + (app?.standardLevelSubjectNumber ?? 0)

  const indexes = Array.from(Array(subjectsToSelectNumber + 1).keys())
  indexes.shift()

  return (
    <div>
      <Dialog open onClose={handleClose}>
        <form>
          <DialogTitle>Subject</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              error={!!formik.errors.name && formik.touched.name}
              helperText={
                !!formik.errors.name && formik.touched.name
                  ? formik.errors.name
                  : null
              }
              margin='dense'
              id='name'
              label='Name'
              type='name'
              fullWidth
              required
              variant='standard'
              value={formik.values.name}
              onChange={formik.handleChange}
            />
            <TextField
              autoFocus
              error={
                !!formik.errors.minSLUsersNumber &&
                formik.touched.minSLUsersNumber
              }
              helperText={
                !!formik.errors.minSLUsersNumber &&
                formik.touched.minSLUsersNumber
                  ? formik.errors.minSLUsersNumber
                  : null
              }
              margin='dense'
              required
              id='minSLUsersNumber'
              label='Minimum SL Group Size'
              type='number'
              fullWidth
              variant='standard'
              value={formik.values.minSLUsersNumber}
              onChange={formik.handleChange}
            />
            <TextField
              autoFocus
              error={
                !!formik.errors.minHLUsersNumber &&
                formik.touched.minHLUsersNumber
              }
              helperText={
                !!formik.errors.minHLUsersNumber &&
                formik.touched.minHLUsersNumber
                  ? formik.errors.minHLUsersNumber
                  : null
              }
              margin='dense'
              required
              id='minHLUsersNumber'
              label='Minimum HL Group Size'
              type='number'
              fullWidth
              variant='standard'
              value={formik.values.minHLUsersNumber}
              onChange={formik.handleChange}
            />
            <FormControl fullWidth style={{ marginTop: '1vw' }}>
              <Autocomplete
                sx={{ m: 1, width: 500 }}
                multiple
                value={formik.values.indexes?.map(i => i.index) ?? []}
                options={indexes}
                getOptionLabel={option => option.toString()}
                isOptionEqualToValue={(a, b) => a === b}
                disableCloseOnSelect
                onChange={(_, _indexes) => {
                  void formik.setFieldValue(
                    'indexes',
                    _indexes.map(i => ({ index: i }))
                  )
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    error={!!formik.errors.indexes && formik.touched.indexes}
                    helperText={
                      !!formik.errors.indexes && formik.touched.indexes
                        ? formik.errors.indexes
                        : null
                    }
                    variant='outlined'
                    label='Subject Groups'
                    placeholder='Indexes'
                    required
                  />
                )}
              />
            </FormControl>
            <TextField
              autoFocus
              margin='dense'
              id='qualification'
              label='Requirements'
              type='name'
              fullWidth
              variant='standard'
              value={formik.values.qualification}
              onChange={formik.handleChange}
            />
            <FormControlLabel
              // style={{ marginLeft: '1vw' }}
              control={
                <Checkbox
                  color='primary'
                  checked={formik.values.isOnlySL}
                  onClick={() => {
                    formik.setFieldValue('isOnlySL', !formik.values.isOnlySL)
                  }}
                />
              }
              label='Only SL'
            />
            <FormControlLabel
              // style={{ marginLeft: '1vw' }}
              control={
                <Checkbox
                  color='primary'
                  checked={formik.values.isOnlyHL}
                  onClick={() => {
                    formik.setFieldValue('isOnlyHL', !formik.values.isOnlyHL)
                  }}
                />
              }
              label='Only HL'
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose as any}>Cancel</Button>
            <Button onClick={formik.handleSubmit as any}>Add</Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  )
}

export default NewOrEditSubjectDialog
